import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search,
  GitBranch,
  ShieldCheck,
  MessageSquare,
  HandshakeIcon,
  Building2,
  FileSearch,
  Bot,
  Code2,
  FileText,
  Users,
  ChevronRight,
} from 'lucide-react';

// Agent data - Step 6
const agentsData = {
  native: [
    {
      id: 'pr-verification-agent',
      name: 'PR Verification Agent',
      category: 'Engineering',
      icon: GitBranch,
      description: 'Analyzes pull requests for code quality, security vulnerabilities, and best practice compliance.',
      workflows: ['PR Governance'],
      integrations: ['GitHub', 'GitLab', 'Bitbucket'],
    },
    {
      id: 'security-audit-agent',
      name: 'Security Audit Agent',
      category: 'Security',
      icon: ShieldCheck,
      description: 'Scans code and infrastructure for security vulnerabilities and compliance issues.',
      workflows: ['Security & Compliance', 'PR Governance'],
      integrations: ['Snyk', 'SonarQube', 'Veracode'],
    },
    {
      id: 'ticket-classification-agent',
      name: 'Ticket Classification Agent',
      category: 'Support',
      icon: MessageSquare,
      description: 'Automatically categorizes and prioritizes support tickets for optimal routing.',
      workflows: ['Customer Support'],
      integrations: ['Zendesk', 'Intercom', 'Freshdesk'],
    },
    {
      id: 'deal-validation-agent',
      name: 'Deal Validation Agent',
      category: 'Sales',
      icon: HandshakeIcon,
      description: 'Reviews deal terms against company policies and pricing guidelines.',
      workflows: ['Sales Deal Desk'],
      integrations: ['Salesforce', 'HubSpot', 'Pipedrive'],
    },
    {
      id: 'vendor-risk-agent',
      name: 'Vendor Risk Agent',
      category: 'Compliance',
      icon: Building2,
      description: 'Assesses vendor financial and operational risk for onboarding and monitoring.',
      workflows: ['Vendor Onboarding'],
      integrations: ['SAP Ariba', 'Coupa', 'Workday'],
    },
    {
      id: 'reviewer-routing-agent',
      name: 'Reviewer Routing Agent',
      category: 'Engineering',
      icon: Users,
      description: 'Assigns optimal reviewers based on expertise, workload, and availability.',
      workflows: ['PR Governance'],
      integrations: ['GitHub', 'GitLab', 'Jira'],
    },
    {
      id: 'knowledge-retrieval-agent',
      name: 'Knowledge Retrieval Agent',
      category: 'Support',
      icon: FileSearch,
      description: 'Searches knowledge bases to find relevant solutions for support issues.',
      workflows: ['Customer Support'],
      integrations: ['Confluence', 'Notion', 'SharePoint'],
    },
    {
      id: 'compliance-policy-agent',
      name: 'Compliance Policy Agent',
      category: 'Compliance',
      icon: FileText,
      description: 'Enforces coding standards, security policies, and regulatory requirements.',
      workflows: ['PR Governance', 'Security & Compliance'],
      integrations: ['Custom Policies', 'SOC 2', 'HIPAA'],
    },
  ],
  community: [
    {
      id: 'contract-review-agent',
      name: 'Contract Review Agent',
      category: 'Legal',
      icon: FileText,
      description: 'Analyzes contract terms to identify risks and non-standard clauses.',
      workflows: ['Vendor Onboarding', 'Sales Deal Desk'],
      integrations: ['DocuSign', 'Ironclad'],
      author: 'LegalTech Inc.',
    },
    {
      id: 'expense-validation-agent',
      name: 'Expense Validation Agent',
      category: 'Finance',
      icon: Code2,
      description: 'Reviews expense reports for policy compliance and fraud detection.',
      workflows: ['Expense Management'],
      integrations: ['Expensify', 'Concur'],
      author: 'FinOps Labs',
    },
  ],
};

const AgentRegistry = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'Engineering', 'Security', 'Support', 'Sales', 'Compliance', 'Legal', 'Finance'];

  const filterAgents = (agents) => {
    return agents.filter((agent) => {
      const matchesSearch = 
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || agent.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  };

  const filteredNative = filterAgents(agentsData.native);
  const filteredCommunity = filterAgents(agentsData.community);

  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      <Helmet>
        <title>Agent Registry | Liquicent</title>
        <meta name="description" content="Discover AI agents that automate enterprise workflows. Browse native and community agents." />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-12 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
              Agent Registry
            </h1>
            <p className="text-xl text-[#475569]">
              Discover AI agents that automate enterprise workflows
            </p>
          </div>

          {/* Search & Filters */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
              <Input
                type="text"
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#F6F7FB] border-[#E5E7EB]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category 
                    ? 'bg-[#1A3A8F] text-white' 
                    : 'border-[#E5E7EB] text-[#475569] hover:bg-[#F6F7FB]'
                  }
                >
                  {category === 'all' ? 'All' : category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Native Agents */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#1A3A8F] flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#0F172A]">Liquicent Native Agents</h2>
              <p className="text-sm text-[#64748B]">{filteredNative.length} agents</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNative.map((agent) => (
              <AgentCard key={agent.id} agent={agent} />
            ))}
          </div>

          {filteredNative.length === 0 && (
            <p className="text-center text-[#64748B] py-12">No agents found matching your criteria.</p>
          )}
        </div>
      </section>

      {/* Community Agents */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#2CB1A1] flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#0F172A]">Community Agents</h2>
              <p className="text-sm text-[#64748B]">{filteredCommunity.length} agents</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCommunity.map((agent) => (
              <AgentCard key={agent.id} agent={agent} isCommunity />
            ))}
          </div>

          {filteredCommunity.length === 0 && (
            <p className="text-center text-[#64748B] py-12">No community agents found matching your criteria.</p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F6F7FB]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Build Your Own Agent</h2>
          <p className="text-[#475569] mb-6">
            Use our SDK to build custom agents and publish them to the registry
          </p>
          <Link to="/developers/publish-agent">
            <Button className="bg-[#1A3A8F] hover:bg-[#142D70] text-white">
              Start Building
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

const AgentCard = ({ agent, isCommunity = false }) => {
  const AgentIcon = agent.icon;
  
  return (
    <Link to={`/agents/${agent.id}`}>
      <Card 
        className="p-6 bg-white border border-[#E5E7EB] rounded-xl hover:shadow-lg hover:border-[#1A3A8F]/20 transition-all h-full group"
        style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
      >
        <div className="flex items-start justify-between mb-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
            style={{ backgroundColor: isCommunity ? '#2CB1A110' : '#1A3A8F10' }}
          >
            <AgentIcon className="w-6 h-6" style={{ color: isCommunity ? '#2CB1A1' : '#1A3A8F' }} />
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-xs font-medium px-2 py-1 bg-[#F6F7FB] rounded-full text-[#64748B]">
              {agent.category}
            </span>
            {!isCommunity && (
              <span className="text-[10px] font-medium px-2 py-0.5 bg-[#1A3A8F]/10 text-[#1A3A8F] rounded-full">
                Newton Engine
              </span>
            )}
            {isCommunity && (
              <span className="text-[10px] font-medium px-2 py-0.5 bg-[#2CB1A1]/10 text-[#2CB1A1] rounded-full">
                Community
              </span>
            )}
          </div>
        </div>
        
        <h3 className="font-semibold text-[#0F172A] mb-2 group-hover:text-[#1A3A8F] transition-colors">
          {agent.name}
        </h3>
        <p className="text-sm text-[#475569] mb-4 line-clamp-2">{agent.description}</p>
        
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {agent.workflows.slice(0, 2).map((wf) => (
              <span key={wf} className="text-xs px-2 py-1 bg-[#1A3A8F]/5 text-[#1A3A8F] rounded">
                {wf}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            {agent.integrations.slice(0, 3).map((int) => (
              <span key={int} className="text-xs text-[#94A3B8]">
                {int}{agent.integrations.indexOf(int) < Math.min(2, agent.integrations.length - 1) ? ' •' : ''}
              </span>
            ))}
          </div>
        </div>

        {isCommunity && agent.author && (
          <p className="mt-4 pt-4 border-t border-[#E5E7EB] text-xs text-[#64748B]">
            By {agent.author}
          </p>
        )}

        {/* Try Agent Button */}
        <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
          <span className="text-xs font-medium text-[#2CB1A1] inline-flex items-center group-hover:text-[#1A3A8F]">
            Try Agent →
          </span>
        </div>
      </Card>
    </Link>
  );
};

export default AgentRegistry;
